<template>
  <div class="ai-statistics-page">
    <!-- 原生/轻依赖版 UI：用于紧急回退，确保数据可见 -->
    <div v-if="useNativeUI" class="ai-statistics-native">
      <!-- 顶部 -->
      <div class="native-header" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <div>
          <h2 class="title">科目AI分析历史</h2>
          <small class="sub">分析AI科目推荐历史，提供优化建议，并可生成文档导入知识库</small>
        </div>
        <div class="btn-group">
          <button class="btn" @click="setQuickRange(7)"><i class="fa fa-calendar"></i> 近7天</button>
          <button class="btn" @click="setQuickRange(30)"><i class="fa fa-calendar-o"></i> 近30天</button>
          <button class="btn" @click="setQuickRange(90)"><i class="fa fa-calendar-check-o"></i> 近90天</button>
          <button class="btn" @click="setAllTime"><i class="fa fa-list"></i> 全部</button>
          <button class="btn btn-primary" style="margin-left:8px;" @click="refreshData"><i class="fa fa-refresh"></i> 刷新数据</button>
        </div>
      </div>

      <!-- 概览 -->
      <div class="native-overview" style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:12px;">
        <div class="kpi-box kpi processing">
          <div class="icon"><i class="fa fa-bar-chart"></i></div>
          <div class="info">
            <div class="num">{{ overviewStats.totalProcessed }}</div>
            <div class="label">总处理量</div>
          </div>
        </div>
        <div class="kpi-box kpi accuracy">
          <div class="icon"><i class="fa fa-check-circle"></i></div>
          <div class="info">
            <div class="num">{{ fmtPercent(overviewStats.avgAccuracy) }}</div>
            <div class="label">平均准确率</div>
          </div>
        </div>
        <div class="kpi-box kpi time">
          <div class="icon"><i class="fa fa-clock-o"></i></div>
          <div class="info">
            <div class="num">{{ fmtNumber(overviewStats.avgProcessTime, 2) }}s</div>
            <div class="label">平均处理时间</div>
          </div>
        </div>
        <div class="kpi-box kpi success">
          <div class="icon"><i class="fa fa-trophy"></i></div>
          <div class="info">
            <div class="num">{{ fmtPercent(overviewStats.successRate) }}</div>
            <div class="label">成功率</div>
          </div>
        </div>
      </div>

      <!-- 趋势（复用 ECharts） -->
      <div class="native-row" style="display:grid;grid-template-columns:7fr 5fr;gap:12px;margin-bottom:12px;">
        <div style="border:1px solid #eee;border-radius:6px;padding:8px;">
          <div style="font-weight:600;margin-bottom:6px;">处理量趋势</div>
          <div ref="processChart" style="height:300px;"></div>
        </div>
        <div style="border:1px solid #eee;border-radius:6px;padding:8px;">
          <div style="font-weight:600;margin-bottom:6px;">优化建议</div>
          <div v-if="suggestionsLoading">加载建议中...</div>
          <div v-else>
            <div v-if="!sortedFilteredSuggestions.length" style="color:#888;">暂无可用建议</div>
            <ul v-else style="list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:8px;">
              <li v-for="(sug, i) in sortedFilteredSuggestions" :key="'sug-'+i" style="border:1px solid #f0f0f0;border-radius:6px;padding:8px;">
                <div style="display:flex;align-items:center;gap:8px;">
                  <span class="tag" :class="'tag--'+String(sug.type).toLowerCase()">{{ getSuggestionTypeText(sug.type) }}</span>
                  <div class="suggestion-title" style="font-weight:600;flex:1;">{{ sug.title }}</div>
                  <div class="muted">影响：{{ toPercent(sug.estimatedImpact) }}%</div>
                </div>
                <div style="color:#666;margin-top:4px;">{{ sug.description }}</div>
                <div style="margin-top:6px;display:flex;gap:8px;">
                  <button class="btn btn-primary" @click="applySuggestion(sug)" :disabled="sug._status==='applied'">{{ sug._status==='applied' ? '已应用' : '应用建议' }}</button>
                  <button class="btn" @click="approveSuggestion(sug)">通过</button>
                  <button class="btn btn-danger" @click="ignoreSuggestion(sug)">忽略</button>
                  <button class="btn btn-ghost" v-if="canPreviewRule(sug)" @click="previewRule(sug)">预览映射</button>
                  <button class="btn btn-ghost" v-if="sug.evidenceSampleIds" @click="previewEvidence(sug)">查看样本</button>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 准确率趋势 + 错误分析 -->
      <div class="native-row" style="display:grid;grid-template-columns:7fr 5fr;gap:12px;margin-bottom:12px;">
        <div style="border:1px solid #eee;border-radius:6px;padding:8px;">
          <div style="font-weight:600;margin-bottom:6px;">准确率分布</div>
          <div ref="accuracyChart" style="height:300px;"></div>
        </div>
        <div style="border:1px solid #eee;border-radius:6px;padding:8px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
            <div style="font-weight:600;">错误分析</div>
            <button class="btn btn-ghost" @click="generateKnowledgeDoc"><i class="fa fa-file-text-o"></i> 生成文档</button>
          </div>
          <table style="width:100%;border-collapse:collapse;">
            <thead>
              <tr>
                <th style="border-bottom:1px solid #eee;text-align:left;padding:6px;">错误类型</th>
                <th style="border-bottom:1px solid #eee;text-align:right;padding:6px;">出现次数</th>
                <th style="border-bottom:1px solid #eee;text-align:right;padding:6px;">占比</th>
                <th style="border-bottom:1px solid #eee;text-align:left;padding:6px;">描述</th>
                <th style="border-bottom:1px solid #eee;text-align:left;padding:6px;">建议</th>
                <th style="border-bottom:1px solid #eee;text-align:left;padding:6px;">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in errorAnalysis" :key="'err-'+i">
                <td style="padding:6px;">{{ row.errorType }}</td>
                <td style="padding:6px;text-align:right;">{{ row.count }}</td>
                <td style="padding:6px;text-align:right;">{{ fmtPercent(row.percentage) }}</td>
                <td style="padding:6px;">{{ row.description }}</td>
                <td style="padding:6px;">{{ row.suggestion }}</td>
                <td style="padding:6px;"><button @click="applyErrorFilter(row)">应用筛选</button></td>
              </tr>
              <tr v-if="!errorAnalysis || !errorAnalysis.length">
                <td colspan="6" style="padding:10px;color:#999;text-align:center;">暂无数据</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 类型统计占位，复用 ECharts 容器 -->
      <div style="border:1px solid #eee;border-radius:6px;padding:8px;margin-bottom:12px;">
        <div style="font-weight:600;margin-bottom:6px;">处理类型统计</div>
        <div ref="typeChart" style="height:300px;"></div>
      </div>

      <!-- 详细处理记录（原生表格） -->
      <div style="border:1px solid #eee;border-radius:6px;padding:8px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;gap:8px;flex-wrap:wrap;">
          <div style="font-weight:600;">详细处理记录</div>
          <div style="display:flex;gap:6px;">
            <button class="btn" @click="batchGenerateSelectedNative"><i class="fa fa-magic"></i> 批量AI生成(已选)</button>
            <button class="btn" @click="batchGenerateAllCurrentPage"><i class="fa fa-clone"></i> 批量AI生成(当前页全部)</button>
            <button class="btn btn-ghost" @click="exportDetailData"><i class="fa fa-download"></i> 导出数据</button>
          </div>
        </div>
        <!-- 简化筛选 -->
        <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:8px;">
          <select class="select" v-model="filterForm.type">
            <option value="">全部类型</option>
            <option value="bank_receipts">银行回单处理</option>
            <option value="bill">票据处理</option>
            <option value="subject_match">科目匹配</option>
          </select>
          <select class="select" v-model="filterForm.status">
            <option value="">全部状态</option>
            <option value="success">成功</option>
            <option value="failed">失败</option>
            <option value="review">需审核</option>
          </select>
          <input class="input" v-model.number="filterForm.minAccuracy" type="number" min="0" max="100" placeholder="最低准确率" style="width:120px;" />
          <input class="input" v-model.number="filterForm.minProcessTime" type="number" min="0" max="3600" placeholder="最小处理时长(秒)" style="width:160px;" />
          <input class="input" v-model="filterForm.keyword" placeholder="关键词搜索" style="width:180px;" />
          <button class="btn btn-primary" @click="loadDetailData"><i class="fa fa-search"></i> 搜索</button>
          <button class="btn" @click="resetFilter">重置</button>
        </div>

        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr>
              <th style="border-bottom:1px solid #eee;text-align:center;padding:6px;width:36px;">
                <input type="checkbox" :checked="isAllChecked" @change="toggleAll($event)" />
              </th>
              <th style="border-bottom:1px solid #eee;text-align:left;padding:6px;">ID</th>
              <th style="border-bottom:1px solid #eee;text-align:left;padding:6px;">处理类型</th>
              <th style="border-bottom:1px solid #eee;text-align:left;padding:6px;">单据编号</th>
              <th style="border-bottom:1px solid #eee;text-align:right;padding:6px;">金额</th>
              <th style="border-bottom:1px solid #eee;text-align:right;padding:6px;">准确率</th>
              <th style="border-bottom:1px solid #eee;text-align:right;padding:6px;">处理时间</th>
              <th style="border-bottom:1px solid #eee;text-align:left;padding:6px;">状态</th>
              <th style="border-bottom:1px solid #eee;text-align:left;padding:6px;">创建时间</th>
              <th style="border-bottom:1px solid #eee;text-align:left;padding:6px;">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in detailData" :key="'detail-'+row.id">
              <td style="padding:6px;text-align:center;">
                <input type="checkbox" v-model="row._checked" @change="syncSelected()" />
              </td>
              <td style="padding:6px;">{{ row.id }}</td>
              <td style="padding:6px;">{{ getTypeText(row.type) }}</td>
              <td style="padding:6px;">{{ row.documentNo }}</td>
              <td style="padding:6px;text-align:right;">{{ Number(row.amount||0).toFixed(2) }}</td>
              <td style="padding:6px;text-align:right;">{{ row.accuracy }}%</td>
              <td style="padding:6px;text-align:right;">{{ row.processTime }}s</td>
              <td style="padding:6px;">{{ getStatusText(row.status) }}</td>
              <td style="padding:6px;">{{ row.createTime }}</td>
              <td style="padding:6px;">
                <button class="btn btn-text" @click="openHistoryDetail(row)">查看详情</button>
                <button class="btn btn-text" @click="openHistoryTrace(row)">查看轨迹</button>
                <button class="btn btn-text" @click="jumpToAiGenerate(row)">用AI生成</button>
              </td>
            </tr>
            <tr v-if="!detailLoading && (!detailData || !detailData.length)">
              <td colspan="9" style="padding:10px;color:#999;text-align:center;">暂无数据</td>
            </tr>
          </tbody>
        </table>
        <div style="display:flex;justify-content:flex-end;align-items:center;gap:8px;margin-top:8px;">
          <span>共 {{ pagination.total }} 条</span>
          <button :disabled="pagination.current<=1" @click="handlePrevPage">上一页</button>
          <span>{{ pagination.current }}</span>
          <button :disabled="detailData.length < pagination.pageSize" @click="handleNextPage">下一页</button>
        </div>
      </div>
    </div>

    <!-- HeyUI 版（默认隐藏，待恢复时可切换） -->
    <div v-else>
    <!-- 页面头部 -->
    <div class="page-header">
      <h-card class="header-card">
        <div class="header-content">
          <div class="title-section">
            <h1>科目ai分析历史记录和优化建议</h1>
            <p>分析AI科目推荐历史，提供优化建议，并可生成文档导入知识库</p>
          </div>
          <div class="time-filter">
            <div class="quick-range">
              <h-button size="small" @click="setQuickRange(7)">近7天</h-button>
              <h-button size="small" @click="setQuickRange(30)">近30天</h-button>
              <h-button size="small" @click="setQuickRange(90)">近90天</h-button>
              <h-button size="small" @click="setAllTime">全部</h-button>
            </div>
            <h-button type="primary" @click="refreshData" style="margin-left: 8px;">刷新数据</h-button>
          </div>
        </div>
      </h-card>
    </div>

    <!-- 概览统计卡片（横向紧凑排列） -->
    <div class="overview-section">
      <h-card class="kpi-card">
        <div class="kpi-container">
          <div class="kpi">
            <div class="kpi-icon processing-icon"><i class="h-icon-file-text"></i></div>
            <div class="kpi-info">
              <div class="kpi-number">{{ overviewStats.totalProcessed }}</div>
              <div class="kpi-label">总处理量</div>
              <div class="kpi-trend" :class="getTrendClass(overviewStats.processedTrend)"><i :class="getTrendIcon(overviewStats.processedTrend)"></i>{{ Math.abs(overviewStats.processedTrend) }}%</div>
              </div>
                </div>
          <div class="kpi">
            <div class="kpi-icon accuracy-icon"><i class="h-icon-check-circle"></i></div>
            <div class="kpi-info">
              <div class="kpi-number">{{ overviewStats.avgAccuracy }}%</div>
              <div class="kpi-label">平均准确率</div>
              <div class="kpi-trend" :class="getTrendClass(overviewStats.accuracyTrend)"><i :class="getTrendIcon(overviewStats.accuracyTrend)"></i>{{ Math.abs(overviewStats.accuracyTrend) }}%</div>
              </div>
            </div>
          <div class="kpi">
            <div class="kpi-icon time-icon"><i class="h-icon-clock"></i></div>
            <div class="kpi-info">
              <div class="kpi-number">{{ overviewStats.avgProcessTime }}s</div>
              <div class="kpi-label">平均处理时间</div>
              <div class="kpi-trend" :class="getTrendClass(overviewStats.timeTrend)"><i :class="getTrendIcon(overviewStats.timeTrend)"></i>{{ Math.abs(overviewStats.timeTrend) }}%</div>
              </div>
                </div>
          <div class="kpi">
            <div class="kpi-icon success-icon"><i class="h-icon-trophy"></i></div>
            <div class="kpi-info">
              <div class="kpi-number">{{ overviewStats.successRate }}%</div>
              <div class="kpi-label">成功率</div>
              <div class="kpi-trend" :class="getTrendClass(overviewStats.successTrend)"><i :class="getTrendIcon(overviewStats.successTrend)"></i>{{ Math.abs(overviewStats.successTrend) }}%</div>
                </div>
              </div>
            </div>
          </h-card>
    </div>

    <!-- 第一行：处理量趋势 + 优化建议 -->
    <h-row :gutter="24">
      <h-col :span="14">
        <h-card title="处理量趋势" class="chart-card">
          <template slot="extra">
            <h-radio-group v-model="processChartType" size="small">
              <h-radio-button value="daily">按日</h-radio-button>
              <h-radio-button value="weekly">按周</h-radio-button>
              <h-radio-button value="monthly">按月</h-radio-button>
            </h-radio-group>
          </template>
          <div class="chart-container" ref="processChart"></div>
        </h-card>
      </h-col>
      <h-col :span="10">
        <h-card title="优化建议" class="suggestion-section">
          <template slot="extra">
            <h-button size="small" @click="exportKnowledgeDoc">导出知识库</h-button>
            <h-button size="small" :loading="suggestionsLoading" @click="loadSuggestions" style="margin-left: 8px;">刷新建议</h-button>
          </template>
          <div v-if="suggestionsLoading" class="suggestions-loading">加载建议中...</div>
          <div v-else>
            <div v-if="!suggestions || suggestions.length === 0" class="suggestions-empty">暂无可用建议。</div>
          <div class="suggestion-toolbar" style="display:flex;gap:8px;margin-bottom:8px;">
            <h-select v-model="suggestionSort" :datas="sortOptions" size="small" style="width:140px;" placeholder="排序方式" />
            <h-select v-model="suggestionTypeFilter" :datas="typeOptions" size="small" style="width:120px;" placeholder="类型过滤" />
          </div>
            <div class="suggestion-list">
              <div class="suggestion-item" v-for="(sug, idx) in sortedFilteredSuggestions" :key="idx">
                <div class="suggestion-head">
                  <h-tag :type="getSuggestionTagType(sug.type)" size="small">{{ getSuggestionTypeText(sug.type) }}</h-tag>
                  <div class="suggestion-title">{{ sug.title }}</div>
                  <div class="suggestion-impact">预估影响：{{ toPercent(sug.estimatedImpact) }}%</div>
                </div>
                <div class="suggestion-desc">{{ sug.description }}</div>
                <div class="suggestion-actions">
                  <h-button size="small" type="primary" @click="applySuggestion(sug)" :disabled="sug._status==='applied'">{{ sug._status==='applied' ? '已应用' : '应用建议' }}</h-button>
                  <h-button size="small" @click="approveSuggestion(sug)">通过</h-button>
                  <h-button size="small" @click="ignoreSuggestion(sug)">忽略</h-button>
                  <h-button size="small" v-if="canPreviewRule(sug)" @click="previewRule(sug)">预览映射</h-button>
                <h-button size="small" v-if="sug.evidenceSampleIds" @click="previewEvidence(sug)">查看样本</h-button>
                </div>
                <div v-if="sug.updatedAt || sug.createdAt" style="color:#999;font-size:12px;margin-top:6px;">更新时间：{{ (sug.updatedAt || sug.createdAt) | datetime }}</div>
              </div>
            </div>
          </div>
        </h-card>
      </h-col>
    </h-row>

    <!-- 第二行：准确率分布 + 错误分析 -->
    <h-row :gutter="24" style="margin-top: 24px;">
      <h-col :span="14">
        <h-card title="准确率分布" class="chart-card">
          <template slot="extra">
            <h-select v-model="accuracyChartType" :datas="accuracyChartOptions" size="small" style="width: 100px;" />
          </template>
          <div class="chart-container" ref="accuracyChart"></div>
        </h-card>
      </h-col>
      <h-col :span="10">
        <h-card title="错误分析" class="error-analysis-card">
          <template slot="extra">
            <h-button size="small" @click="generateKnowledgeDoc">生成文档</h-button>
          </template>
          <h-table :data="errorAnalysis" size="small">
            <h-table-column prop="errorType" label="错误类型" width="150" />
            <h-table-column prop="count" label="出现次数" width="100" align="right" />
            <h-table-column prop="percentage" label="占比" width="80" align="right">
              <template slot-scope="{ row }">{{ row.percentage }}%</template>
            </h-table-column>
            <h-table-column prop="description" label="描述" />
            <h-table-column prop="suggestion" label="改进建议" />
            <h-table-column label="操作" width="120">
              <template slot-scope="{ row }">
                <h-button size="small" @click="applyErrorFilter(row)">应用筛选</h-button>
              </template>
            </h-table-column>
          </h-table>
        </h-card>
      </h-col>
    </h-row>

    <!-- 第三行：处理类型统计 -->
    <div style="margin-top: 24px;">
      <h-card title="处理类型统计" class="chart-card">
        <div class="chart-container" ref="typeChart"></div>
      </h-card>
    </div>

    <!-- 详细数据表格 -->
    <div class="detail-section">
      <h-card title="详细处理记录" class="detail-card">
        <template slot="extra">
          <h-button @click="batchJumpToAiGenerate" :disabled="!selectedRows.length" type="primary" style="margin-right:8px;">批量用AI生成</h-button>
          <h-button @click="exportDetailData">导出数据</h-button>
        </template>

        <!-- 筛选条件 -->
        <div class="filter-section">
          <h-row :gutter="16">
            <h-col :span="4">
              <h-select v-model="filterForm.type" :datas="typeFilterOptions" placeholder="处理类型" />
            </h-col>
            <h-col :span="4">
              <h-select v-model="filterForm.status" :datas="statusFilterOptions" placeholder="处理状态" />
            </h-col>
            <h-col :span="4">
              <h-input-number
                v-model="filterForm.minAccuracy"
                placeholder="最低准确率"
                :min="0"
                :max="100"
                style="width: 100%;"
              />
            </h-col>
            <h-col :span="4">
              <h-input-number
                v-model="filterForm.maxAccuracy"
                placeholder="最高准确率"
                :min="0"
                :max="100"
                style="width: 100%;"
              />
            </h-col>
            <h-col :span="4">
              <h-input-number
                v-model="filterForm.minProcessTime"
                placeholder="最小处理时长(秒)"
                :min="0"
                :max="3600"
                style="width: 100%;"
              />
            </h-col>
            <h-col :span="4">
              <h-input
                v-model="filterForm.keyword"
                placeholder="关键词搜索"
                clearable
              />
            </h-col>
            <h-col :span="4">
              <h-button type="primary" @click="loadDetailData">搜索</h-button>
              <h-button @click="resetFilter">重置</h-button>
            </h-col>
          </h-row>
        </div>

        <!-- 数据表格 -->
        <h-table
          :data="detailData"
          :loading="detailLoading"
          @selection-change="onSelectionChange"
          stripe
        >
          <h-table-column type="selection" width="48" />
          <h-table-column prop="id" label="ID" width="80" />
          <h-table-column prop="type" label="处理类型" width="100">
            <template slot-scope="{ row }">
              <h-tag :type="getTypeTagType(row.type)">
                {{ getTypeText(row.type) }}
              </h-tag>
            </template>
          </h-table-column>
          <h-table-column prop="documentNo" label="单据编号" width="150" />
          <h-table-column prop="amount" label="金额" width="120" align="right">
            <template slot-scope="{ row }">
              ¥{{ formatAmount(row.amount) }}
            </template>
          </h-table-column>
          <h-table-column prop="accuracy" label="准确率" width="100" align="right">
            <template slot-scope="{ row }">
              <span :class="getAccuracyClass(row.accuracy)">
                {{ row.accuracy }}%
              </span>
            </template>
          </h-table-column>
          <h-table-column prop="processTime" label="处理时间" width="100" align="right">
            <template slot-scope="{ row }">
              {{ row.processTime }}s
            </template>
          </h-table-column>
          <h-table-column prop="status" label="状态" width="100">
            <template slot-scope="{ row }">
              <h-tag :type="getStatusTagType(row.status)">
                {{ getStatusText(row.status) }}
              </h-tag>
            </template>
          </h-table-column>
          <h-table-column prop="createTime" label="创建时间" width="150" />
          <h-table-column label="操作" width="200" fixed="right">
            <template slot-scope="{ row }">
              <h-button size="small" type="text" @click="viewDetail(row)">查看详情</h-button>
              <h-button size="small" type="text" @click="jumpToAiGenerate(row)">用AI生成</h-button>
            </template>
          </h-table-column>
        </h-table>

        <!-- 分页 -->
        <div class="pagination-wrapper">
          <h-pagination
            :total="pagination.total"
            :current="pagination.current"
            :size="pagination.pageSize"
            @change="handlePageChange"
            show-total
            show-sizer
            :size-options="[10, 20, 50, 100]"
            @sizechange="handleSizeChange"
          />
        </div>
      </h-card>
    </div>
    </div>
  </div>
</template>

<script>
import { formatAmount } from '@/utils/format'
import aiAnalysisApi from '@/api/ai-analysis-history'
// 显式引入常用HeyUI子组件，避免热替换时的注册时序告警
// 注：HeyUI 已在全局 main.js 中通过 Vue.use(HeyUI) 注册，无需在此单独导入子组件

export default {
  name: 'AiAnalysisHistory',
  data() {
    return {
      useNativeUI: true,
      detailLoading: false,
      dateRange: [],
      rawDateRange: [], // 已去除日期控件，仅保留程序内部使用（快捷键设置时更新）
      processChartType: 'daily',
      accuracyChartType: 'pie',
      suggestionsLoading: false,
      suggestions: [],
      suggestionSort: 'impact',
      suggestionTypeFilter: '',
      sortOptions: [
        { key: 'impact', title: '按影响排序' },
        { key: 'time', title: '按时间排序' }
      ],
      typeOptions: [
        { key: '', title: '全部类型' },
        { key: 'rule', title: '规则' },
        { key: 'prompt', title: '提示词' },
        { key: 'threshold', title: '阈值' }
      ],
      accuracyChartOptions: [
        { key: 'pie', title: '饼图' },
        { key: 'bar', title: '柱状图' }
      ],
      typeFilterOptions: [
        { key: '', title: '全部' },
        { key: 'bank_receipts', title: '银行回单处理' },
        { key: 'bill', title: '票据处理' },
        { key: 'subject_match', title: '科目匹配' }
      ],
      statusFilterOptions: [
        { key: '', title: '全部' },
        { key: 'success', title: '成功' },
        { key: 'failed', title: '失败' },
        { key: 'review', title: '需审核' }
      ],
      suggestionsAgg: { total: 0, applied: 0, impact: 0 },
      overviewStats: {
        totalProcessed: 0,
        processedTrend: 0,
        avgAccuracy: 0,
        accuracyTrend: 0,
        avgProcessTime: 0,
        timeTrend: 0,
        successRate: 0,
        successTrend: 0
      },
      errorAnalysis: [],
      detailData: [],
      filterForm: {
        type: '',
        status: '',
        minAccuracy: null,
        maxAccuracy: null,
        minProcessTime: null,
        keyword: ''
      },
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0
      },
        selectedRows: [],
      processChart: null,
      accuracyChart: null,
      trendX: [],
      trendTotal: [],
      trendSuccess: [],
      trendConfidence: [],
      selectedRows: []
    }
  },
  computed: {
    sortedFilteredSuggestions() {
      let arr = Array.isArray(this.suggestions) ? this.suggestions.slice() : []
      if (this.suggestionTypeFilter) {
        arr = arr.filter(s => String(s.type).toLowerCase() === String(this.suggestionTypeFilter).toLowerCase())
      }
      if (this.suggestionSort === 'time') {
        arr.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0))
      } else {
        arr.sort((a, b) => (Number(b.estimatedImpact) || 0) - (Number(a.estimatedImpact) || 0))
      }
      return arr
    }
  },
  mounted() {
    this.initDateRange()
    this.loadOverviewStats()
    this.loadErrorAnalysis()
    this.loadDetailData()
    this.loadSuggestions()
    this.$nextTick(() => {
      this.initCharts()
      this.loadTrendAndRender()
    })
  },
  methods: {
    isChecked(row) { return !!row._checked },
    get isAllChecked() { return (this.detailData||[]).length>0 && (this.detailData||[]).every(r=>r._checked) },
    toggleAll(e) {
      const checked = !!e.target.checked
      (this.detailData || []).forEach(r => { r._checked = checked })
      this.syncSelected()
    },
    syncSelected() {
      this.selectedRows = (this.detailData || []).filter(r => r._checked)
    },
    batchGenerateSelectedNative() {
      if (!this.selectedRows.length) { this.$Message.info('请先勾选记录'); return }
      const bills = []
      const receipts = []
      this.selectedRows.forEach(r => {
        const t = r.businessType || r.type
        if ((t === 'bank_receipts' || t === 'receipt' || t === 'RECEIPT') && r.businessId) receipts.push(r.businessId)
        else if ((t === 'bill' || t === 'DOCUMENT') && r.businessId) bills.push(r.businessId)
      })
      if (receipts.length) window.open(`/ai/subject-enhancement?mode=receipt&ids=${encodeURIComponent(receipts.join(','))}`, '_blank')
      if (bills.length) window.open(`/ai/subject-enhancement?mode=bill&ids=${encodeURIComponent(bills.join(','))}`, '_blank')
    },
    batchGenerateAllCurrentPage() {
      const bills = []
      const receipts = []
      ;(this.detailData || []).forEach(r => {
        const t = r.businessType || r.type
        if ((t === 'bank_receipts' || t === 'receipt' || t === 'RECEIPT') && r.businessId) receipts.push(r.businessId)
        else if ((t === 'bill' || t === 'DOCUMENT') && r.businessId) bills.push(r.businessId)
      })
      if (!bills.length && !receipts.length) { this.$Message.info('当前页无可用业务ID'); return }
      if (receipts.length) window.open(`/ai/subject-enhancement?mode=receipt&ids=${encodeURIComponent(receipts.join(','))}`, '_blank')
      if (bills.length) window.open(`/ai/subject-enhancement?mode=bill&ids=${encodeURIComponent(bills.join(','))}`, '_blank')
    },
    async openHistoryTrace(row) {
      try {
        let trace = row.trace || row.traceJson
        if (!trace) {
          try {
            const res = await aiAnalysisApi.getTrace(row.id)
            trace = (res && res.data) ? res.data : res
          } catch (e) {
            trace = null
          }
        }
        if (!trace) {
          const t = row.businessType || row.type
          trace = {
            historyId: row.id,
            trace: [
              { type: t === 'RECEIPT' ? 'ingest_receipt' : 'ingest_document', ts: row.createTime, summary: '入库完成' },
              { type: 'rule_match', ts: row.createTime, summary: `规则/关键词初匹配 → ${Array.isArray(row.matchedSubjects)? row.matchedSubjects.join(','): (row.matchedSubjects||'-')}` },
              { type: 'llm_infer', ts: row.createTime, summary: `LLM推理，置信度 ${row.accuracy != null ? row.accuracy+'%' : '-'}` },
              { type: 'preview', ts: row.createTime, summary: '生成预览结果' }
            ]
          }
        }
        const steps = Array.isArray(trace.trace) ? trace.trace : []
        const icon = (tp) => {
          switch (tp) {
            case 'ingest_document': return '📄'
            case 'ingest_receipt': return '🏦'
            case 'group_create': return '🗂️'
            case 'relation_create': return '🔗'
            case 'rule_match': return '✅'
            case 'prompt_build': return '🧩'
            case 'llm_infer': return '🤖'
            case 'preview': return '📝'
            case 'human_feedback': return '🧍'
            case 'confirm': return '📌'
            default: return '⏺️'
          }
        }
        const html = steps.map((s, i) => {
          const head = `${icon(s.type)} [${(s.type||'').replace('_',' ')}] ${s.summary || ''}`
          const body = s.details ? `<pre style=\"white-space:pre-wrap;margin:6px 0 0;\">${typeof s.details==='string'? s.details : JSON.stringify(s.details, null, 2)}</pre>` : ''
          const met = s.metrics ? `<div style=\"color:#999;font-size:12px;\">metrics: ${JSON.stringify(s.metrics)}</div>` : ''
          return `<li style=\"margin:8px 0;\"><div style=\"font-weight:600;\">${head}</div>${body}${met}</li>`
        }).join('')
        const content = html ? `<ol style=\"margin:0;padding-left:18px;\">${html}</ol>` : '<div style=\"color:#999;\">暂无轨迹</div>'
        this.$Modal({ title: 'AI处理轨迹', content })
      } catch (e) {
        this.$Message.error('加载轨迹失败')
      }
    },
    openHistoryDetail(row) {
      try {
        const t = row.businessType || row.type
        const subjects = Array.isArray(row.matchedSubjects) ? row.matchedSubjects.join(', ') : (row.matchedSubjects || '-')
        const conf = row.accuracy != null ? `${row.accuracy}%` : '-'
        const reasoning = row.aiReasoning || '（无AI推理说明）'
        const lines = [
          `ID: ${row.id}`,
          `业务类型: ${t}`,
          `业务ID: ${row.businessId || '-'}`,
          `创建时间: ${row.createTime || '-'}`,
          `置信度: ${conf}`,
          `处理时间: ${row.processTime != null ? row.processTime + 's' : '-'}`,
          `匹配科目: ${subjects}`,
          '',
          'AI处理过程:',
          reasoning
        ].join('\n')
        this.$Modal({
          title: 'AI处理详情',
          content: `<pre style="white-space:pre-wrap;max-height:60vh;overflow:auto;">${lines}</pre>`
        })
      } catch (e) {
        alert('无法显示详情')
      }
    },
    fmtNumber(v, digits = 2) {
      const n = Number(v)
      if (Number.isNaN(n)) return '0'
      return n.toFixed(digits)
    },
    fmtPercent(v, digits = 2) {
      const n = Number(v)
      if (Number.isNaN(n)) return '0%'
      // 支持输入 0-1 或 0-100
      const val = n <= 1 ? n * 100 : n
      return `${val.toFixed(digits)}%`
    },
    handlePrevPage() {
      if (this.pagination.current > 1) {
        this.pagination.current -= 1
        this.loadDetailData()
      }
    },
    handleNextPage() {
      this.pagination.current += 1
      this.loadDetailData()
    },
    // 兼容 axios 封装返回 {data: ...} 或直接返回对象/数组
    getPayload(res) {
      if (res && typeof res === 'object' && Object.prototype.hasOwnProperty.call(res, 'data')) {
        return res.data
      }
      return res
    },
    /**
     * 初始化日期范围（默认最近30天）
     */
    initDateRange() {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      this.rawDateRange = [start, end];
      this.dateRange = [this.formatDate(start), this.formatDate(end)];
    },
      
    formatDate(date) {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    },

    /**
     * 处理日期变化
     */
    // 日期选择器已移除，保留方法兼容（不再使用）
    handleDateChange() {},

    setQuickRange(days) {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - days);
      this.rawDateRange = [start, end]
      this.dateRange = [this.formatDate(start), this.formatDate(end)]
      this.refreshData();
    },
    setAllTime() {
      // 查询全量：传 null 以后端默认
      this.rawDateRange = []
      this.dateRange = []
      this.refreshData();
    },

    /**
     * 刷新数据
     */
    refreshData() {
      this.loadOverviewStats()
      this.loadErrorAnalysis()
      this.loadDetailData()
      this.loadSuggestions()
      this.updateCharts()
      this.loadTrendAndRender()
    },

    /**
     * 加载概览统计
     */
    async loadOverviewStats() {
      try {
        const params = {
          startDate: this.dateRange[0] ? new Date(this.dateRange[0]).getTime() : null,
          endDate: this.dateRange[1] ? new Date(this.dateRange[1]).getTime() : null,
        }
        const response = await aiAnalysisApi.overview(params)
        const payload = this.getPayload(response)
        if (payload) {
          this.overviewStats = payload
        }
      } catch (error) {
        console.error('加载概览统计失败:', error)
      }
    },

    /**
     * 加载错误分析
     */
    async loadErrorAnalysis() {
      try {
        const params = {
          startDate: this.dateRange[0] ? new Date(this.dateRange[0]).getTime() : null,
          endDate: this.dateRange[1] ? new Date(this.dateRange[1]).getTime() : null,
        }
        const response = await aiAnalysisApi.errorAnalysis(params)
        const payload = this.getPayload(response)
        if (payload) {
          this.errorAnalysis = payload
        }
      } catch (error) {
        console.error('加载错误分析失败:', error)
      }
    },

    /**
     * 加载详细数据
     */
    async loadDetailData() {
      this.detailLoading = true
      try {
        const params = {
          page: this.pagination.current,
          size: this.pagination.pageSize,
          businessType: this.filterForm.type,
          status: this.filterForm.status,
          minAccuracy: this.normalizeAccuracy(this.filterForm.minAccuracy),
          maxAccuracy: this.normalizeAccuracy(this.filterForm.maxAccuracy),
          minProcessTime: this.filterForm.minProcessTime,
          keyword: this.filterForm.keyword,
          startDate: this.dateRange[0] ? new Date(this.dateRange[0]).getTime() : null,
          endDate: this.dateRange[1] ? new Date(this.dateRange[1]).getTime() : null,
        }

        const response = await aiAnalysisApi.history(params)
        const page = this.getPayload(response) || {}
        if (page) {
          const records = Array.isArray(page.records) ? page.records : []
          // 兼容后端实体字段，映射成表格展示所需字段
          this.detailData = records.map((it) => {
            const accuracyRaw = it.accuracy != null ? it.accuracy : (it.confidenceScore != null ? Number(it.confidenceScore) : null)
            const accuracy = accuracyRaw == null ? null : (accuracyRaw <= 1 ? Math.round(accuracyRaw * 100) : Math.round(accuracyRaw))
            const processTime = it.processTime != null ? it.processTime : (it.processingTime != null ? it.processingTime : null)
            const status = it.status || (it.userFeedback != null ? (it.userFeedback >= 1 ? 'success' : 'review') : 'review')
            return {
              id: it.id,
              type: it.type || it.businessType,
              documentNo: it.documentNo || it.receiptsNo || it.billNo || '-',
              amount: it.amount || 0,
              accuracy,
              processTime,
              status,
              createTime: it.createTime,
              businessId: it.businessId || it.sourceId || it.id,
              businessType: it.businessType || it.type,
            }
          })
          this.pagination.total = page.total || 0
          this.tableKey = (this.tableKey || 0) + 1
        }
      } catch (error) {
        this.$Message.error('加载详细数据失败')
        console.error('加载详细数据失败:', error)
      } finally {
        this.detailLoading = false
      }
    },

    /**
     * 重置筛选条件
     */
    resetFilter() {
      this.filterForm = {
        type: '',
        status: '',
        minAccuracy: null,
        maxAccuracy: null,
        minProcessTime: null,
        keyword: ''
      }
      this.pagination.current = 1
      this.loadDetailData()
    },

    onSelectionChange(rows) {
      this.selectedRows = rows || []
    },

    /**
     * 处理页面大小变化
     */
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.current = 1
      this.loadDetailData()
    },
    handlePageChange(page) {
      this.pagination.current = page
      this.loadDetailData()
    },

    /**
     * 查看详情
     */
    viewDetail(row) {
      // 根据业务类型导航到对应的详情页面
      const t = row.businessType || row.type
      if (t === 'bank_receipts' || t === 'receipt' || t === 'RECEIPT') {
        if (row.businessId) {
          this.$router.push(`/bills/bank-receipts/form/${row.businessId}`)
        }
        return
      }
      if (t === 'bill' || t === 'DOCUMENT') {
        if (row.businessId) {
          this.$router.push(`/bills/bill-form/${row.businessId}`)
        }
        return
      }
      this.$Message.info('暂无可跳转的详情页面')
    },
    jumpToAiGenerate(row) {
      const t = row.businessType || row.type
      const id = row.businessId
      if (!id) { this.$Message.info('缺少业务ID'); return }
      if (t === 'bank_receipts' || t === 'receipt' || t === 'RECEIPT') {
        this.$router.push({ path: '/ai/subject-enhancement', query: { mode: 'receipt', ids: id } })
        return
      }
      if (t === 'bill' || t === 'DOCUMENT') {
        this.$router.push({ path: '/ai/subject-enhancement', query: { mode: 'bill', ids: id } })
        return
      }
      this.$router.push('/ai/subject-enhancement')
    },
    batchJumpToAiGenerate() {
      if (!this.selectedRows.length) { this.$Message.info('请先勾选记录'); return }
      const bills = []
      const receipts = []
      this.selectedRows.forEach(r => {
        const t = r.businessType || r.type
        if ((t === 'bank_receipts' || t === 'receipt' || t === 'RECEIPT') && r.businessId) receipts.push(r.businessId)
        else if ((t === 'bill' || t === 'DOCUMENT') && r.businessId) bills.push(r.businessId)
      })
      if (receipts.length) {
        this.$router.push({ path: '/ai/subject-enhancement', query: { mode: 'receipt', ids: receipts.join(',') } })
      }
      if (bills.length) {
        this.$router.push({ path: '/ai/subject-enhancement', query: { mode: 'bill', ids: bills.join(',') } })
      }
    },

    /**
     * 生成知识库文档
     */
    generateKnowledgeDoc() {
      this.$Message.info('正在生成知识库文档...')
      // 未来可以实现具体的文档生成逻辑，例如调用API生成word或markdown文件
      setTimeout(() => {
        this.$Message.success('知识库文档已生成，请在下载内容中查看。')
      }, 2000)
    },

    /**
     * 导出详细数据
     */
    exportDetailData() {
      const params = new URLSearchParams()
      if (this.filterForm.type) params.append('businessType', this.filterForm.type)
      if (this.filterForm.status) params.append('status', this.filterForm.status)
      if (this.filterForm.minAccuracy != null) params.append('minAccuracy', this.normalizeAccuracy(this.filterForm.minAccuracy))
      if (this.filterForm.maxAccuracy != null) params.append('maxAccuracy', this.normalizeAccuracy(this.filterForm.maxAccuracy))
      if (this.filterForm.minProcessTime != null) params.append('minProcessTime', this.filterForm.minProcessTime)
      if (this.filterForm.keyword) params.append('keyword', this.filterForm.keyword)
      if (this.dateRange[0]) params.append('startDate', new Date(this.dateRange[0]).getTime())
      if (this.dateRange[1]) params.append('endDate', new Date(this.dateRange[1]).getTime())
      const url = `/api/ai-analysis-history/export?${params.toString()}`
      window.open(url, '_blank')
    },

    normalizeAccuracy(val) {
      if (val == null || val === '') return null
      const num = Number(val)
      if (isNaN(num)) return null
      return num > 1 ? Math.round((num / 100) * 1000) / 1000 : num
    },

    applyErrorFilter(row) {
      const t = (row && row.errorType) || ''
      if (t.includes('低置信度')) {
        this.filterForm.maxAccuracy = 85
        this.filterForm.minProcessTime = null
        this.filterForm.status = ''
      } else if (t.includes('需审核')) {
        this.filterForm.status = 'review'
        this.filterForm.maxAccuracy = null
        this.filterForm.minProcessTime = null
      } else if (t.includes('超时')) {
        this.filterForm.minProcessTime = 5
        this.filterForm.maxAccuracy = null
        this.filterForm.status = ''
      }
      this.pagination.current = 1
      this.loadDetailData()
      this.loadTrendAndRender()
      this.$Message.success('已根据错误类型应用筛选')
    },

    /**
     * 加载建议
     */
    async loadSuggestions() {
      this.suggestionsLoading = true
      try {
        const params = {
          startDate: this.dateRange[0] ? new Date(this.dateRange[0]).getTime() : null,
          endDate: this.dateRange[1] ? new Date(this.dateRange[1]).getTime() : null,
        }
        const resp = await aiAnalysisApi.suggestions(params)
        const arr = Array.isArray(resp) ? resp : []
        this.suggestions = arr.map(s => ({ ...s, _status: s.status }))
        this.computeSuggestionAgg()
      } catch (e) {
        console.error('加载建议失败', e)
      } finally {
        this.suggestionsLoading = false
      }
    },

    applySuggestion(sug) {
      aiAnalysisApi.suggestionApply(sug).then(() => {
        this.$Message.success('已应用建议')
        if (sug) {
          this.$set(sug, '_status', 'applied')
          this.computeSuggestionAgg()
          this.loadOverviewStats()
        }
      }).catch(() => {
        this.$Message.error('应用建议失败')
      })
    },
    approveSuggestion(sug) {
      aiAnalysisApi.suggestionApprove(sug).then(() => {
        this.$Message.success('已通过建议')
        sug._status = 'approved'
      }).catch(() => this.$Message.error('通过建议失败'))
    },
    ignoreSuggestion(sug) {
      aiAnalysisApi.suggestionIgnore(sug).then(() => {
        this.$Message.success('已忽略建议')
        sug._status = 'ignored'
        this.computeSuggestionAgg()
      }).catch(() => this.$Message.error('忽略建议失败'))
    },
    exportKnowledgeDoc() {
      const params = new URLSearchParams()
      if (this.dateRange[0]) params.append('startDate', new Date(this.dateRange[0]).getTime())
      if (this.dateRange[1]) params.append('endDate', new Date(this.dateRange[1]).getTime())
      window.open(`/api/ai-analysis-history/kb/export?${params.toString()}`, '_blank')
    },

    computeSuggestionAgg() {
      const total = this.suggestions.length
      const applied = this.suggestions.filter(s => s._status === 'applied').length
      const impact = Math.round(this.suggestions.reduce((sum, s) => sum + (Number(s.estimatedImpact) || 0), 0))
      this.suggestionsAgg = { total, applied, impact }
    },
    canPreviewRule(sug) {
      return sug && String(sug.type).toLowerCase() === 'rule' && sug.payloadJson
    },
    previewRule(sug) {
      try {
        const payload = JSON.parse(sug.payloadJson || '{}')
        const mappings = payload.keywordMappings || []
        if (!mappings.length) {
          this.$Message.info('无可预览映射')
          return
        }
        const lines = mappings.map(m => `${m.keyword} → ${m.subjectCode}`).join('\n')
        this.$Modal({ title: '规则映射预览', content: `<pre style=\\"white-space:pre-wrap\\">${lines}</pre>` })
      } catch (e) {
        this.$Message.error('解析映射失败')
      }
    },
    previewEvidence(sug) {
      try {
        const ids = (sug.evidenceSampleIds || '').split(',').map(x => x.trim()).filter(Boolean)
        if (!ids.length) { this.$Message.info('无样本可查看'); return }
        // 拉取样本摘要详情
        fetch(`/api/ai-analysis-history/history/by-ids?ids=${encodeURIComponent(ids.join(','))}`, { credentials: 'include' })
          .then(r => r.json())
          .then(res => {
            const rows = Array.isArray(res) ? res : (res.data || [])
            if (!rows.length) { this.$Message.info('暂无样本详情'); return }
            const html = rows.map(it => {
              const t = String(it.businessType || '-')
              const sub = (it.matchedSubjects || '').toString()
              const conf = it.confidenceScore != null ? (Number(it.confidenceScore) <= 1 ? Math.round(Number(it.confidenceScore)*100) : Math.round(Number(it.confidenceScore))) : '-'
              const sec = it.processingTime != null ? `${it.processingTime}s` : '-'
              const bid = it.businessId || it.id
              const isReceipt = (t === 'bank_receipts' || t === 'receipt' || t === 'RECEIPT')
              const detailPath = isReceipt ? `/bills/bank-receipts/form/${bid}` : `/bills/bill-form/${bid}`
              const genPath = isReceipt ? `/ai/subject-enhancement?mode=receipt&ids=${bid}` : `/ai/subject-enhancement?mode=bill&ids=${bid}`
              return `
                <div style="padding:8px 0;border-bottom:1px solid #f0f0f0;">
                  <div style="font-weight:600;color:#333;">[#${it.id}] ${t}</div>
                  <div style="color:#666;">置信度: ${conf}% | 用时: ${sec}</div>
                  <div style="color:#999;white-space:pre-wrap;">科目: ${sub}</div>
                  <div style="margin-top:6px;">
                    <a href="${detailPath}" target="_blank" style="margin-right:12px;">查看详情</a>
                    <a href="${genPath}" target="_blank">用AI生成</a>
                  </div>
                </div>`
            }).join('')
            this.$Modal({ title: '证据样本', content: `<div>${html}</div>` })
          })
          .catch(() => {
            const lines = ids.map(id => `样本ID: ${id}`).join('\n')
            this.$Modal({ title: '证据样本', content: `<pre style=\"white-space:pre-wrap\">${lines}</pre>` })
          })
      } catch (e) {
        this.$Message.error('加载样本失败')
      }
    },
    toPercent(v) {
      const n = Number(v)
      if (isNaN(n)) return 0
      return n > 1 ? Math.round(n) : Math.round(n * 100)
    },

    /**
     * 初始化图表
     */
    initCharts() {
      if (!this.$echarts) {
        try {
          // 延迟引入，避免全局体积增加
          this.$echarts = require('echarts')
        } catch (e) {
          console.warn('ECharts 未安装，使用占位渲染')
      this.initProcessChart()
      this.initAccuracyChart()
      this.initTypeChart()
          return
        }
      }
      this.processChart = this.$echarts.init(this.$refs.processChart)
      this.accuracyChart = this.$echarts.init(this.$refs.accuracyChart)
      if (this.$refs.typeChart) {
        this.typeChart = this.$echarts.init(this.$refs.typeChart)
      }
      window.addEventListener('resize', () => {
        this.processChart && this.processChart.resize()
        this.accuracyChart && this.accuracyChart.resize()
        this.typeChart && this.typeChart.resize()
      })
    },

    /**
     * 初始化处理量趋势图
     */
    initProcessChart() {
      if (!this.processChart) return
      const option = {
        tooltip: { trigger: 'axis' },
        legend: { data: ['处理量', '成功率'] },
        xAxis: { type: 'category', data: this.trendX || [] },
        yAxis: [
          { type: 'value', name: '处理量' },
          { type: 'value', name: '成功率(%)', min: 0, max: 100 }
        ],
        series: [
          { name: '处理量', type: 'bar', data: this.trendTotal || [] },
          { name: '成功率', type: 'line', yAxisIndex: 1, smooth: true, data: this.trendSuccess || [] }
        ]
      }
      this.processChart.setOption(option)
    },

    /**
     * 初始化准确率分布图
     */
    initAccuracyChart() {
      if (!this.accuracyChart) return
      const option = {
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: this.trendX || [] },
        yAxis: { type: 'value', name: '平均置信度' },
        series: [
          { name: '平均置信度', type: 'line', smooth: true, areaStyle: {}, data: this.trendConfidence || [] }
        ]
      }
      this.accuracyChart.setOption(option)
    },

    /**
     * 初始化处理类型统计图
     */
    initTypeChart() {
      if (!this.typeChart) return
      // 基于当前明细数据统计类型分布
      const counts = {}
      ;(this.detailData || []).forEach((row) => {
        const t = row.type || row.businessType || '未知'
        counts[t] = (counts[t] || 0) + 1
      })
      const entries = Object.entries(counts)
      const labels = entries.map(([k]) => this.getTypeText(k))
      const values = entries.map(([, v]) => v)
      const option = {
        tooltip: { trigger: 'item' },
        xAxis: { type: 'category', data: labels },
        yAxis: { type: 'value', name: '数量' },
        series: [
          { type: 'bar', data: values }
        ]
      }
      this.typeChart.setOption(option)
    },

    /**
     * 更新图表
     */
    updateCharts() {
      this.initProcessChart()
      this.initAccuracyChart()
      this.initTypeChart()
    },

    async loadTrendAndRender() {
      try {
        const params = {
          startDate: this.dateRange[0] ? new Date(this.dateRange[0]).getTime() : null,
          endDate: this.dateRange[1] ? new Date(this.dateRange[1]).getTime() : null,
        }
        const resp = await aiAnalysisApi.trendDaily(params)
        const rows = this.getPayload(resp)
        if (!Array.isArray(rows)) {
          console.warn('趋势数据为空')
          return
        }
        this.trendX = rows.map(r => r.date)
        this.trendTotal = rows.map(r => r.total)
        this.trendSuccess = rows.map(r => Math.round((r.successRate || 0)))
        // 置信度既支持0-1也支持0-100，做兼容
        this.trendConfidence = rows.map(r => {
          const v = Number(r.avgConfidence || 0)
          return v <= 1 ? Math.round(v * 100) : Math.round(v)
        })
        this.updateCharts()
      } catch (e) {
        console.error('加载趋势失败', e)
      }
    },

    /**
     * 获取趋势样式类
     */
    getTrendClass(trend) {
      return trend > 0 ? 'trend-up' : 'trend-down'
    },

    /**
     * 获取趋势图标
     */
    getTrendIcon(trend) {
      return trend > 0 ? 'h-icon-arrow-up' : 'h-icon-arrow-down'
    },

    /**
     * 获取类型标签类型
     */
    getTypeTagType(type) {
      switch (type) {
        case 'bank_receipts': return 'primary'
        case 'bill': return 'success'
        case 'subject_match': return 'warning'
        default: return 'info'
      }
    },

    /**
     * 获取类型文本
     */
    getTypeText(type) {
      switch (type) {
        case 'bank_receipts': return '银行回单处理'
        case 'bill': return '票据处理'
        case 'subject_match': return '科目匹配'
        default: return '未知'
      }
    },

    /**
     * 获取状态标签类型
     */
    getStatusTagType(status) {
      switch (status) {
        case 'success': return 'success'
        case 'failed': return 'danger'
        case 'review': return 'warning'
        default: return 'info'
      }
    },

    /**
     * 获取状态文本
     */
    getStatusText(status) {
      switch (status) {
        case 'success': return '成功'
        case 'failed': return '失败'
        case 'review': return '需审核'
        default: return '处理中'
      }
    },

    getSuggestionTagType(type) {
      switch (type) {
        case 'prompt': return 'primary'
        case 'rule': return 'success'
        case 'threshold': return 'warning'
        case 'template': return 'info'
        default: return 'info'
      }
    },
    getSuggestionTypeText(type) {
      switch (type) {
        case 'prompt': return '提示词'
        case 'rule': return '规则'
        case 'threshold': return '阈值'
        case 'template': return '模板'
        default: return '其他'
      }
    },

    /**
     * 获取准确率样式类
     */
    getAccuracyClass(accuracy) {
      if (accuracy >= 95) return 'accuracy-high'
      if (accuracy >= 85) return 'accuracy-medium'
      return 'accuracy-low'
    },

    /**
     * 格式化金额
     */
    formatAmount,

  }
}
</script>

<style lang="less" scoped>
.ai-statistics-page {
  padding: 20px;
  
  /* 原生回退版整体美化 */
  .ai-statistics-native {
    .native-header {
      margin-bottom: 16px;
      .title { margin: 0; font-size: 20px; font-weight: 600; }
      .sub { color: #666; }
      .btn-group { display: flex; gap: 6px; align-items: center; }
    }

    /* 统一按钮风格，贴近 HeyUI 观感 */
    .btn, button {
      padding: 6px 12px;
      border-radius: 4px;
      border: 1px solid #d9d9d9;
      background: #fff;
      color: #333;
      cursor: pointer;
      transition: all .2s;
    }
    .btn:hover, button:hover { border-color: #1890ff; color: #1890ff; }
    .btn[disabled], button[disabled] { cursor: not-allowed; opacity: .6; }
    .btn-primary { background: #1890ff; border-color: #1890ff; color: #fff; }
    .btn-primary:hover { filter: brightness(1.05); color: #fff; }
    .btn-danger { background: #ff4d4f; border-color: #ff4d4f; color: #fff; }
    .btn-danger:hover { filter: brightness(1.05); color: #fff; }
    .btn-ghost { background: #f7f8fa; border-color: #eaecef; color: #333; }
    .btn-text { background: transparent; border-color: transparent; color: #1890ff; padding: 0 4px; }

    /* 卡片风格 */
    .kpi-box,
    .native-card,
    .native-row > div,
    .native-overview > div {
      background: #fff;
      border: 1px solid #f0f0f0;
      border-radius: 8px;
      box-shadow: 0 1px 2px rgba(0,0,0,.03);
    }

    .kpi-box { padding: 12px; }
    .native-row > div { padding: 12px; }

    /* KPI 更美观的双色块 */
    .kpi { display: flex; align-items: center; gap: 10px; }
    .kpi .icon { width: 42px; height: 42px; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 18px; }
    .kpi.processing .icon { background: linear-gradient(135deg,#667eea 0%,#764ba2 100%); }
    .kpi.accuracy .icon { background: linear-gradient(135deg,#f093fb 0%,#f5576c 100%); }
    .kpi.time .icon { background: linear-gradient(135deg,#4facfe 0%,#00f2fe 100%); }
    .kpi.success .icon { background: linear-gradient(135deg,#43e97b 0%,#38f9d7 100%); }
    .kpi .info .num { font-size: 18px; font-weight: 700; color: #333; line-height: 1; margin-bottom: 2px; }
    .kpi .info .label { font-size: 12px; color: #666; }

    /* 表格美化 */
    table { width: 100%; border-collapse: collapse; background: #fff; }
    thead th {
      background: #fafafa;
      color: #555;
      font-weight: 600;
      border-bottom: 1px solid #f0f0f0;
      padding: 10px 8px;
      text-align: left;
      font-size: 13px;
    }
    tbody td { padding: 10px 8px; border-bottom: 1px solid #f5f5f5; font-size: 13px; }
    tbody tr:hover { background: #fafafa; }

    /* 建议列表 */
    .suggestion-item { background: #fff; }
    .suggestion-item .suggestion-title { font-size: 14px; }
    .suggestion-item .suggestion-desc { color: #666; }
    .muted { color: #666; font-size: 12px; }
    .tag { display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 12px; color: #fff; }
    .tag--rule { background: #52c41a; }
    .tag--prompt { background: #1890ff; }
    .tag--threshold { background: #faad14; }

    /* 原生输入 */
    .select, .input { padding: 6px 8px; border: 1px solid #d9d9d9; border-radius: 4px; background: #fff; }
  }
  
  .page-header {
    margin-bottom: 24px;
    
    .header-card {
      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        .title-section {
          h1 {
            margin: 0 0 8px 0;
            font-size: 24px;
            font-weight: 600;
            color: #1890ff;
          }
          
          p {
            margin: 0;
            color: #666;
            font-size: 14px;
          }
        }
      }
    }
  }
  
  .overview-section {
    margin-bottom: 16px;
    /* 紧凑KPI布局 */
    .kpi-card { padding: 0; }
    .kpi-container { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 12px; padding: 8px 12px 12px; }
    .kpi { display:flex; align-items:center; background:#fff; border:1px solid #f0f0f0; border-radius:8px; padding:10px 12px; }
    .kpi-icon { width:48px; height:48px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-right:12px; }
    .kpi-icon i { font-size:22px; color:#fff; }
    .kpi-icon.processing-icon { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
    .kpi-icon.accuracy-icon { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); }
    .kpi-icon.time-icon { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
    .kpi-icon.success-icon { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); }
    .kpi-info { flex:1; min-width:0; }
    .kpi-number { font-size:22px; font-weight:700; color:#333; line-height:1; margin-bottom:4px; }
    .kpi-label { font-size:12px; color:#666; margin-bottom:2px; }
    .kpi-trend { font-size:12px; font-weight:600; }
    .trend-up { color:#52c41a; }
    .trend-down { color:#ff4d4f; }
  }

  /* 修正时间过滤区样式位置 */
  .time-filter { display:flex; align-items:center; gap:8px; }
  .time-filter .quick-range { display:flex; gap:6px; margin-right:8px; }
  
  .chart-card {
    .chart-container {
      height: 300px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f8f9fa;
      border-radius: 4px;
      color: #999;
      font-size: 14px;
    }
  }
  
  .error-analysis-card {
    .h-table {
      font-size: 12px;
    }
  }
  
  .detail-section {
    margin-top: 24px;
    
    .detail-card {
      .filter-section {
        margin-bottom: 16px;
        padding: 16px;
        background: #f8f9fa;
        border-radius: 4px;
      }
      
      .pagination-wrapper {
        margin-top: 16px;
        text-align: right;
      }
      
      .accuracy-high {
        color: #52c41a;
        font-weight: 600;
      }
      
      .accuracy-medium {
        color: #faad14;
        font-weight: 600;
      }
      
      .accuracy-low {
        color: #ff4d4f;
        font-weight: 600;
      }
    }
  }

  .suggestion-section {
    .suggestion-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .suggestion-item {
      padding: 12px;
      border: 1px solid #f0f0f0;
      border-radius: 6px;
      background: #fff;
    }
    .suggestion-head {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
    }
    .suggestion-title {
      font-weight: 600;
      color: #333;
      flex: 1;
    }
    .suggestion-impact {
      color: #999;
      font-size: 12px;
    }
    .suggestion-desc {
      color: #666;
      margin-bottom: 8px;
    }
    .suggestion-actions {
      display: flex;
      gap: 8px;
    }
    .suggestions-empty {
      color: #999;
    }
  }
}
</style>
